import { __awaiter } from 'tslib';
import { TypeMismatchError, ErrorCode, OpenFeatureEventEmitter, GeneralError, ProviderEvents } from '@openfeature/web-sdk';
import { GrowthBook } from '@growthbook/growthbook';
import isEmpty from 'lodash.isempty';

const FEATURE_RESULT_ERRORS = ['unknownFeature', 'cyclicPrerequisite'];
function translateError(errorKind) {
    switch (errorKind) {
        case 'unknownFeature':
            return ErrorCode.FLAG_NOT_FOUND;
        case 'cyclicPrerequisite':
            return ErrorCode.PARSE_ERROR;
        default:
            return ErrorCode.GENERAL;
    }
}
function translateResult(result, defaultValue) {
    var _a;
    if (result.value !== null && typeof result.value !== typeof defaultValue) {
        throw new TypeMismatchError(`Expected flag type ${typeof defaultValue} but got ${typeof result.value}`);
    }
    const resolution = {
        value: result.value === null ? defaultValue : result.value,
        reason: result.source,
        variant: (_a = result.experimentResult) === null || _a === void 0 ? void 0 : _a.key,
    };
    if (FEATURE_RESULT_ERRORS.includes(result.source)) {
        resolution.errorCode = translateError(result.source);
    }
    return resolution;
}

class GrowthbookClientProvider {
    constructor(growthbookContext, initOptions) {
        this.metadata = {
            name: GrowthbookClientProvider.name,
        };
        this.runsOn = 'client';
        this.events = new OpenFeatureEventEmitter();
        this.context = growthbookContext;
        this._initOptions = initOptions;
    }
    get client() {
        if (!this._client) {
            throw new GeneralError('Provider is not initialized');
        }
        return this._client;
    }
    // the global context is passed to the initialization function
    initialize(evalContext) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use context to construct the instance to instantiate GrowthBook
            this._client = new GrowthBook(this.context);
            if (!isEmpty(evalContext)) {
                // Set attributes from the global provider context
                yield this.client.setAttributes(evalContext);
            }
            yield this.client.init(this._initOptions);
            // Monkey-patch the setPayload function to fire an event
            const setPayload = this._client.setPayload.bind(this._client);
            this._client.setPayload = (...args) => __awaiter(this, void 0, void 0, function* () {
                yield setPayload(...args);
                this.events.emit(ProviderEvents.ConfigurationChanged);
            });
        });
    }
    onClose() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.client.destroy();
        });
    }
    onContextChange(oldContext, newContext) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.client.setAttributes(newContext);
        });
    }
    resolveBooleanEvaluation(flagKey, defaultValue) {
        const res = this.client.evalFeature(flagKey);
        return translateResult(res, defaultValue);
    }
    resolveStringEvaluation(flagKey, defaultValue) {
        const res = this.client.evalFeature(flagKey);
        return translateResult(res, defaultValue);
    }
    resolveNumberEvaluation(flagKey, defaultValue) {
        const res = this.client.evalFeature(flagKey);
        return translateResult(res, defaultValue);
    }
    resolveObjectEvaluation(flagKey, defaultValue) {
        const res = this.client.evalFeature(flagKey);
        return translateResult(res, defaultValue);
    }
}

export { GrowthbookClientProvider };
